// !!IMPORTANT!!: these utils should be used server side only, or they will expose sensitive data.

import Cache from "memory-cache";
import { type MarketoFormDataType, type MarketoFormField as MarketoFormFieldType } from "~/features/marketo/types";
const MARKETO_API_HEADERS_KEY = "MARKETO_API_HEADERS";
let hasWarnedMissingMarketoCredentials = false;

// TODO: this is just a temporary fix to map SOAP fields to REST fields
export const MARKETO_FIELD_MAP = {
  FirstName: "firstName",
  LastName: "lastName",
  Email: "email",
  Phone: "phone",
  Company: "company",
  Title: "title",
  Country: "country",
  State: "state",
  MKTO_Industry__c: "MKTO_Industry__c",
  Product_Group__c: "Product_Group__c",
  Product_List_JSON__c: "Product_List_JSON__c",
  Message__c: "Message__c",
  lTUTMSource: "lTUTMSource",
  lTUTMCampaign: "lTUTMCampaign",
  lTUTMContent: "lTUTMContent",
  lTUTMMedium: "lTUTMMedium",
  lTUTMTerm: "lTUTMTerm"
} as Record<string, string>;
export const MARKETO_UTM_FIELDS = ["lTUTMSource", "lTUTMMedium", "lTUTMCampaign", "lTUTMContent", "lTUTMTerm"];
export const MARKETO_UTM_MAP = {
  lTUTMSource: "utm_source",
  lTUTMMedium: "utm_medium",
  lTUTMCampaign: "utm_campaign",
  lTUTMContent: "utm_content",
  lTUTMTerm: "utm_term"
} as Record<string, string>;
function getMarketoCredentials() {
  const clientId = process.env.MARKETO_CLIENT_ID?.trim();
  const clientSecret = process.env.MARKETO_CLIENT_SECRET?.trim();
  if (!clientId || !clientSecret) {
    if (!hasWarnedMissingMarketoCredentials) {
      console.warn("Missing MARKETO_CLIENT_ID or MARKETO_CLIENT_SECRET; Marketo form data will not be preloaded.");
      hasWarnedMissingMarketoCredentials = true;
    }
    return null;
  }
  return {
    clientId,
    clientSecret
  };
}
async function marketoFetchHeaders() {
  const credentials = getMarketoCredentials();
  if (!credentials) {
    throw new Error("Missing Marketo credentials");
  }
  const url = `https://893-FVD-854.mktorest.com/identity/oauth/token?grant_type=client_credentials&client_id=${credentials.clientId}&client_secret=${credentials.clientSecret}`;
  const response = await fetch(url);
  const data = await response.json();
  if (!response.ok || !data.access_token || typeof data.expires_in !== "number") {
    throw new Error(data.errors?.[0]?.message || "Invalid Marketo auth response");
  }
  const expiresInMilliseconds = Math.max((data.expires_in - 10) * 1000, 1000); // subtract 10 seconds to buffer

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${data.access_token}`
  };
  Cache.put(MARKETO_API_HEADERS_KEY, headers, expiresInMilliseconds);
}
export async function marketoGetValidHeaders(): Promise<{
  [key: string]: string;
}> {
  let headers = Cache.get(MARKETO_API_HEADERS_KEY);
  if (!headers) {
    await marketoFetchHeaders();
    headers = Cache.get(MARKETO_API_HEADERS_KEY);
  }
  if (!headers) {
    throw new Error("Failed to obtain Marketo API Headers");
  }
  return headers;
}
export function marketoMapFields(fields: Record<string, string>) {
  const mappedFields: Record<string, string> = {};
  Object.entries(fields).forEach(([key, value]) => {
    const marketoFieldKey = MARKETO_FIELD_MAP[key];
    if (marketoFieldKey) {
      mappedFields[marketoFieldKey] = value;
    }
  });
  return mappedFields;
}
export async function marketoFetchFormData(formId: string) {
  let formData: MarketoFormDataType | null = null;
  try {
    if (!getMarketoCredentials()) {
      return formData;
    }
    const url = `https://893-FVD-854.mktorest.com/rest/asset/v1/form/${formId}.json`;
    const headers = await marketoGetValidHeaders();
    const response = await fetch(url, {
      headers,
      method: "GET"
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.errors?.[0]?.message || "Error fetching form data");
    }
    if (data.result && data.result.length > 0) {
      formData = data.result[0];
    } else {
      throw new Error("Form data not found in the response");
    }
  } catch (e) {
    console.error((e as Error).message);
  }
  return formData;
}
export async function marketoFetchFormFields(formId: string) {
  let fields: MarketoFormFieldType[] = [];
  try {
    if (!getMarketoCredentials()) {
      return fields;
    }
    const url = `https://893-FVD-854.mktorest.com/rest/asset/v1/form/${formId}/fields.json`;
    const headers = await marketoGetValidHeaders();
    const response = await fetch(url, {
      headers,
      method: "GET"
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.errors?.[0]?.message || "Error fetching form fields");
    }
    fields = data.result;
  } catch (e) {
    console.error((e as Error).message);
  }
  return fields;
}

// export function constructLeadFormFields(leadFields: any[], parsedBodyFields: { [key: string]: string }) {
//   return leadFields.reduce((acc: Record<string, string>, field: any) => {
//     if (parsedBodyFields[field.displayName]) {
//       acc[field.name] = parsedBodyFields[field.displayName] || "";
//     }
//     return acc;
//   }, {});
// }

// export function collectInvalidFieldErrors(leadFields: any[], parsedBodyFields: { [key: string]: string }): string[] {
//   const marketoDisplayNames = leadFields.map((field) => field.displayName);

//   return Object.keys(parsedBodyFields).reduce((acc: string[], key) => {
//     if (!marketoDisplayNames.includes(key)) {
//       acc.push(`Field '${key}' doesn't match in Marketo.`);
//     }
//     return acc;
//   }, []);
// }

// /**
//  * Recursively fetches all lead fields from the Marketo API, handling pagination.
//  * The function makes sequential requests using the nextPageToken until there are no more results.
//  *
//  * @param {string | null} nextPageToken - Token for the next page of results. If null, starts from the first page.
//  * @returns {Promise<any[]>} Resolves with the full array of lead fields across all pages.
//  */

// export async function fetchAllLeadFields(nextPageToken: string | null = null): Promise<any[]> {
//   const headers = await marketoGetValidHeaders();

//   const baseURL = "https://893-FVD-854.mktorest.com/rest/v1/leads/describe2.json";
//   const url = nextPageToken ? `${baseURL}?nextPageToken=${nextPageToken}` : baseURL;

//   const leadFieldsResponse = await fetch(url, {
//     headers,
//     method: "GET",
//   });

//   const leadFieldsData = await leadFieldsResponse.json();

//   if (!leadFieldsData.success) {
//     throw new Error(leadFieldsData.errors?.[0]?.message || "Error fetching lead fields");
//   }

//   const leadFields = leadFieldsData.result?.[0]?.fields || [];

//   return leadFieldsData.moreResult
//     ? leadFields.concat(await fetchAllLeadFields(leadFieldsData.nextPageToken))
//     : leadFields;
// }